import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ErrorHandlerService, hd } from '@nx-fe/services';
/*
export class User {
  constructor(public firstname: string, public lastname: string, public selected?: boolean) {
    if (selected === undefined) selected = false;
  }
}*/

@Component({
  selector: 'xs-search-and-pick',
  templateUrl: './search-and-pick.component.html',
  styleUrls: ['./search-and-pick.component.scss'],
})
export class SearchAndPickComponent<T> implements OnInit, OnDestroy {
  @Input('itemTemplate') itemTemplate: TemplateRef<T>;
  @Input('placeholder') placeholder = '';
  @Input('minimumFilterLength') minimumFilterLength: number;
  @Input('queryFn') queryFn: Function;
  @Input('selectedItem') selectedItem: T;
  @Input('queryParameters') queryParameters: any;
  @Input('excludeList') excludeList: any[];
  @Input('excludeSearchParameter') excludeSearchParameter: any;
  @Input('idField') idField = 'ticketId';
  @Input('showSearchAndCancelIcons') showSearchAndCancelIcons = false;

  @Input() set clearTextOnLoad(value: boolean) {
    if (value) {
      this.clearSearch();
      this.setFocus();
    }
  }

  @Input() set clearSearchResults(value: boolean) {
    if (value) {
      this.trigger.closePanel();
      this.clearSearch();
    }
  }

  @Output('onPick') onPick = new EventEmitter<T>();
  @Output('onEntered') onEntered = new EventEmitter<string>();
  @Output('hasSearched') hasSearched = new EventEmitter<boolean>();

  @ViewChild(MatInput, { static: false }) textInput: MatInput;
  @ViewChild(MatAutocomplete, { static: false }) auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger, { static: false }) trigger: MatAutocompleteTrigger;
  @ViewChild('autoCompleteInput', { read: MatAutocompleteTrigger })
  autoComplete: MatAutocompleteTrigger;

  searchForm: FormGroup;
  inputControl = new FormControl();

  filteredItems: Observable<T[]>;
  filteredItemsList: any;
  lastFilter = '';
  searchFilter = '';
  isSearchIconDisplay = true;
  //** Private field to track whether a search is currently in progress */
  private _isSearching: boolean = false;

  searchSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private elRef: ElementRef
  ) {
    this.filteredItems = this.searchSubject.asObservable();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
  scrollEvent = (event: any): void => {
    if (!this.trigger.panelOpen) return;

    this.trigger.updatePosition();

    if (this._getScrollVisibility()) {
      this.trigger.closePanel();
    }
  };

  private _getScrollVisibility(): boolean {
    // Get header position
    const navContainer = document.getElementById('primary-nav');
    let navContainerRect = navContainer.getBoundingClientRect();
    let autoRect;
    if (this.trigger.panelOpen) {
      const autoCompleteContainer = document.getElementById('autoComplete');
      autoRect = autoCompleteContainer.getBoundingClientRect();
    }

    // Check if the autocomplete panel intersects with header
    return this.isElementIntersecting(autoRect, navContainerRect);
  }

  private isElementIntersecting(elementRect, overlayBounds): boolean {
    return (
      elementRect.top < overlayBounds.bottom &&
      elementRect.bottom > overlayBounds.top &&
      elementRect.left < overlayBounds.right &&
      elementRect.right > overlayBounds.left
    );
  }
  ngOnInit() {
    window.addEventListener('scroll', this.scrollEvent, true);

    this.searchForm = this.formBuilder.group({
      inputControl: null,
    });

    this.searchSubscription = this.searchForm
      .get('inputControl')
      .valueChanges.pipe(debounceTime(500))
      .subscribe(value => {
        this.filterChanged(value);
      });
  }

  private searchSubject = new Subject<T[]>();
  public filterChanged = (filter: string): Observable<T[]> => {
    const searchSubject = this.searchSubject;
    if (!filter) {
      this.searchSubject.next([]);
    }
    if (this.minimumFilterLength === undefined) {
      // default if not specified in markup
      this.minimumFilterLength = 1;
    }

    if (this.minimumFilterLength === 0 || (filter && filter.length >= this.minimumFilterLength)) {
      // Add the IDs of the things to exclude from the search results
      const excludeIds = this.getExcludeIds();
      const searchParams = { searchText: filter };
      this._isSearching = true;
      if (this.queryParameters) {
        for (const key in this.queryParameters) {
          if (this.queryParameters.hasOwnProperty(key)) {
            searchParams[key] = this.queryParameters[key];
          }
        }
      }

      if (this.excludeSearchParameter === undefined) {
        // default if not specified in markup
        this.excludeSearchParameter = 'excludeIds';
      }

      if (excludeIds) {
        searchParams[this.excludeSearchParameter] = excludeIds.toString();
      } else {
        searchParams[this.excludeSearchParameter] = null;
      }

      this.queryFn(searchParams, (searchResults: any[], responseHeaders?: any) => {
        const warningMessage: string = responseHeaders ? responseHeaders('sa-warning-message') : null;
        if (warningMessage) {
          const warning: hd.IError = {
            error_description: warningMessage,
          };
          this.errorHandlerService.setError(warning);
        }
        this._isSearching = false;
        searchSubject.next(searchResults);
        this.hasSearched.emit(true);
        this.selectItem(undefined);
        if (filter != this.lastFilter) {
          this.lastFilter = filter;
        }
      });
    }
    this._isSearching = false;
    this.filteredItems.subscribe(result => {
      this.filteredItemsList = result;
    });
    return this.filteredItems;
  };

  searchEmpty() {
    return (
      this.filteredItemsList !== undefined &&
      this.filteredItemsList.length === 0 &&
      this.searchInputText.value.length > 0
    );
  }

  /** Searches the exclude list and returns a list of item IDs to exclude */
  public getExcludeIds = () => {
    let excludeIds;
    const excludeList = this.excludeList;
    if (excludeList && excludeList.length > 0) {
      excludeIds = [];
      excludeList.forEach(excludeItem => {
        if (!!excludeItem) {
          if (typeof excludeItem === 'string') {
            excludeIds.push(excludeItem);
          } else if (excludeItem[this.idField]) {
            excludeIds.push(excludeItem[this.idField]);
          }
        }
      }, this);
    }
    return excludeIds;
  };

  /** Gets a value indicating whether a search is currently in progress */
  public isSearching(): boolean {
    return this._isSearching;
  }
  public getGlyphicon(selection: any) {
    let result = '';
    switch (selection.status) {
      case 'open':
        result = 'hd-icon hd-icon-open text-status-open status-icon-open';
        break;
      case 'pending':
        result = 'hd-icon hd-icon-more status-icon-pending';
        break;
      case 'resolved':
        result = 'hd-icon hd-icon-accept status-icon-resolved';
        break;
    }

    return result;
  }

  displayWrapped = (value: any) => (!!value ? this.displayFn(value.item, undefined) : '');

  displayFn = (value: T[] | T, fieldName: string): string => {
    if (this.searchInputText.value === '') {
      return;
    }
    if (fieldName === undefined) {
      fieldName = this.idField;
    }
    const display = (item: T) => {
      if (!item) {
        return '';
      }
      if (item[fieldName]) {
        return item[fieldName];
      }

      return item.toString();
    };

    let displayValue: string;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (index === 0) {
          displayValue = display(item);
        } else {
          displayValue += ', ' + display(item);
        }
      });
    } else {
      displayValue = display(value);
    }

    return displayValue;
  };

  optionClicked(selection) {
    this.selectItem(selection);
    this.clearSearch();
    this.setFocus(); // Set focus to the search input after the selection is made
  }

  private selectItem(selection) {
    this.searchFilter = this.displayFn(selection, this.idField);
    this.selectedItem = selection;
    this.onPick.emit(selection);
    if (selection != undefined) {
      //this.searchForm.get('inputControl').setValue('');
      this.clearSearch();
    }
  }

  public clearSearch = (): void => {
    if (this.searchInputText) {
      this.searchInputText.setValue('');
      this.searchSubject.next([]);
      this.trigger.closePanel();
    }
  };

  public onFocusOutEvent = (event: any): void => {
    if (!event.relatedTarget || event.relatedTarget.id !== 'autoComplete') {
      this.trigger.closePanel();
    }

    if (!this.auto.isOpen) {
      this.submitValue();
    }
  };

  public submitValue = (): void => {
    const value = this.searchForm.get('inputControl').value;
    if (value && value.length > 0) {
      this.onEntered.emit(value);
      this.clearSearch();
    }
  };

  public setFocus = () => {
    const searchInputElement = this.elRef.nativeElement.querySelector('.searchInput');
    if (searchInputElement) {
      this.removeSearchIcon();
      setTimeout(() => {
        searchInputElement.focus();
      });
    }
  };

  public removeSearchIcon() {
    if (this.showSearchAndCancelIcons) {
      this.isSearchIconDisplay = false;
    }
  }

  public get searchInputText() {
    return this.searchForm ? this.searchForm.get('inputControl') : null;
  }
}
